// DataScienceTraining.tsx

import React, { useState } from 'react'; // Import useState
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

// Import icons for the 'Why Choose Us' section (similar to Digital Marketing page)
import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';

import JoinNowFormPopup from "../../componants/JoinNowFormPopup"; // Assuming this path is correct

// Placeholder imports for data science-related images/logos
// You'll need to replace these with actual paths to your images
// import dsHeroImg from "../../assets/Internship/data_science_hero.png"; // A new hero image for data science
import pythonLogo from "../../assets/Internship/Python_logo.png"; // Example: path to Python logo
// import rLogo from "../../assets/Internship/r_logo.png"; // Example: path to R logo
import tensorflowLogo from "../../assets/Internship/tensorflow.png"; // Example: path to TensorFlow logo
import scikitLearnLogo from "../../assets/Internship/scikit_learn.png"; // Example: path to Scikit-learn logo
import pandasLogo from "../../assets/Internship/pandas.png"; // Example: path to Pandas logo
import tableauLogo from "../../assets/Internship/tableau.png"; // Example: path to Tableau logo

import processImg from "../../assets/Internship/our-process.webp"; // Using the same process image for now

// Animation variants (reusing from Digital Marketing page)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const DataScienceTraining: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
  const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title

  const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
    setSelectedCourseTitle(courseTitle); // Set the selected course title
    setIsPopupOpen(true); // Open the popup
  };

  const handleClosePopup = () => { // Function to close the popup
    setIsPopupOpen(false); // Close the popup
    setSelectedCourseTitle(""); // Clear selected course when closing
  };

  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-orange-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-orange-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-orange-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-orange-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-orange-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-orange-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>Data Science Training in Noida | Appwars Technologies</title>
        <meta name="description" content="Unlock the power of data with comprehensive industrial training in Noida. Master Python, R, Machine Learning, Deep Learning, and Data Visualization. 100% placement, hybrid classes, and internship certificates by Appwars Technologies." />
        <meta name="keywords" content="data science, Python, R, machine learning, deep learning, data visualization, statistics, analytics, AI, Noida, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="Data Science Training in Noida | Appwars Technologies" />
        <meta property="og:description" content="Boost your career with comprehensive Data Science training programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/data-science-noida" /> {/* Replace with your actual website URL */}
        <link rel="canonical" href="http://yourwebsite.com/data-science-noida" /> {/* Replace with your actual canonical URL */}
      </Helmet>


      <main>
        {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
        <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
          {/* Transparent Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://media.istockphoto.com/id/1364317541/photo/data-scientists-hand-of-programmer-touching-and-analyzing-development-at-various-information.jpg?s=612x612&w=0&k=20&c=mTnPYtYYxbyOdyeBQcu8LIWwD-31SIIfXSd0IEhhpYg=" // Using a generic technology background image
              alt="Data Science Transparent Background"
              className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
            />
            {/* Optional: Add a subtle overlay for better text readability and darker background */}
            <div className="absolute inset-0 bg-black opacity-50"></div> {/* Adjust opacity as needed */}
          </div>

          {/* Text and Image Content */}
          <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
            <motion.div
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                Data Science Training
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Unlock the power of data with Nadezhda Venture's comprehensive hands-on program. Master skills to analyze, interpret, and leverage data for informed decision-making.
              </p>

              <button
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                onClick={() => handleOpenPopup("Data Science Training")} // Pass the course title
              >
                Join Now !
              </button>
            </motion.div>

            {/* <motion.div
              className="flex-1 w-full max-w-2xl flex justify-center items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <img
                src={dsHeroImg} // Placeholder for a data science-specific hero image
                alt="Data Science Showcase"
                className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
              />
            </motion.div> */}
          </div>
        </section>


        {/* Key Data Science Technologies & Concepts Section */}
        <section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
          <h2 className="text-5xl font-bold text-center mb-10">Key Data Science Technologies & Concepts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {/* Python */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={pythonLogo} alt="Python Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Python</h3>
              <button
                onClick={() => handleOpenPopup("Python for Data Science")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* R
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={rLogo} alt="R Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">R</h3>
              <button
                onClick={() => handleOpenPopup("R for Data Science")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div> */}

            {/* TensorFlow */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={tensorflowLogo} alt="TensorFlow Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">TensorFlow</h3>
              <button
                onClick={() => handleOpenPopup("TensorFlow Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Scikit-learn */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={scikitLearnLogo} alt="Scikit-learn Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Scikit-learn</h3>
              <button
                onClick={() => handleOpenPopup("Scikit-learn Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Pandas */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={pandasLogo} alt="Pandas Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Pandas</h3>
              <button
                onClick={() => handleOpenPopup("Pandas for Data Analysis")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Tableau */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={tableauLogo} alt="Tableau Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Tableau</h3>
              <button
                onClick={() => handleOpenPopup("Tableau Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

          </div>
        </section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-orange-700">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us for Data Science Training?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-blue-950 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>


        {/* Our Training & Placement Process Section */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Training & Placement Process</h2>
        <img
          src={processImg} // Path to your process image file
          alt="Our Training and Placement Process"
          className="w-full h-auto object-cover"
        />

      </main>

      {/* Floating Contact Us button */}
      {/* <a
        href="#"
        className="fixed bottom-8 right-8 bg-green-500 text-white text-lg font-semibold py-3 px-6 rounded-full shadow-xl hover:bg-green-600 transition-colors duration-300 z-50 transform hover:scale-105"
        onClick={(e) => {
          e.preventDefault(); // Prevent default link behavior
          handleOpenPopup("General Data Science Inquiry"); // Open with a general inquiry specific to Data Science
        }}
      >
        Contact Us
      </a> */}

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default DataScienceTraining;